import { useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Label, Information, ToggleDiv } from '../StyledComponents';
import ToggleSwitch from '../../../../shared/FormStyles/ToggleSwitch.jsx';
import TextInput from '../../../../shared/FormStyles/TextInput.jsx';
import Tooltip from '../../../../shared/Tooltip/Tooltip.jsx';

const GuestAccessSettings = ({
  settings,
  handleChange,
  setFieldValue,
  handleBlur,
  inputError,
  setFieldError,
  setFieldTouched,
}) => {
  const { t } = useTranslation('boardSetup');
  const maxCharacters = 250;

  useEffect(() => {
    if (settings.useEmployeeId && !settings.employeeIdBody?.trim()) {
      setFieldTouched('employeeIdBody', true);
      setFieldError(
        'employeeIdBody',
        t('boardSetup:formValidation.emptyEmployeeIdBody'),
      );
    }
  }, [
    settings.useEmployeeId,
    settings.employeeIdBody,
    setFieldTouched,
    setFieldError,
    t,
  ]);

  const handleAllowGuestUserChange = value => {
    setFieldValue('guestUserConversionModal', value);
    if (!value) {
      setFieldValue('useEmployeeId', false);
    }
  };

  const handleHideWelcomeModalSignIn = value => {
    setFieldValue('hideWelcomeModalSignIn', !value);
  };

  const toogleLabel = value => {
    return value ? t('sharing.enabled') : t('sharing.disabled');
  };

  return (
    <GuestAccessBox>
      <Tooltip
        id="guestAccessSettings"
        getContent={dataTip => (
          <div dangerouslySetInnerHTML={{ __html: dataTip }} />
        )}
      />
      <Title>
        <TitleText>{t('sharing.guestAccessTitle')}</TitleText>
      </Title>

      <Description>{t('sharing.guestAccessDescription')}</Description>
      <SettingRow>
        <SettingLabel>
          {t('sharing.signInOption')}
          <Information
            className="fa-regular fa-circle-info fa-sm"
            data-tip={t('sharing.signInOptionDataTip')}
          />
        </SettingLabel>
        <ToggleDiv>
          <ToggleSwitch
            value={!settings.hideWelcomeModalSignIn}
            onChange={e => handleHideWelcomeModalSignIn(e.target.checked)}
            name="hideWelcomeModalSignIn"
            id="sign-in-option"
            label={toogleLabel(!settings.hideWelcomeModalSignIn)}
          />
        </ToggleDiv>
      </SettingRow>

      <SettingRow>
        <SettingLabel>
          {t('sharing.accountCreation')}
          <Information
            className="fa-regular fa-circle-info fa-sm"
            data-tip={t('sharing.accountCreationDataTip')}
          />
        </SettingLabel>
        <ToggleDiv>
          <ToggleSwitch
            value={settings.guestUserConversionModal}
            onChange={e => handleAllowGuestUserChange(e.target.checked)}
            name="guestUserConversionModal"
            id="account-creation"
            label={toogleLabel(settings.guestUserConversionModal)}
          />
        </ToggleDiv>
      </SettingRow>

      <SettingEmployeeIdRow>
        <EmployeeIdLabel>
          {t('sharing.requireEmployeeId')}
          <Information
            className="fa-regular fa-circle-info fa-sm"
            data-tip={t('sharing.requireEmployeeIdDataTip')}
            data-for="guestAccessSettings"
          />
        </EmployeeIdLabel>
        <ToggleDiv>
          <ToggleSwitch
            value={settings.useEmployeeId}
            onChange={handleChange}
            name="useEmployeeId"
            id="require-employee-id"
            disabled={!settings.guestUserConversionModal}
            label={toogleLabel(settings.useEmployeeId)}
          />
        </ToggleDiv>
      </SettingEmployeeIdRow>
      {settings.useEmployeeId && (
        <>
          <SeparatorLine />
          <Description>{t('sharing.employeeIdSupport')}</Description>
          {/* <InputWrapper>
            <TextInput
              label={t('sharing.employeeIdHeader')}
              id="employeeIdHeader"
              name="employeeIdHeader"
              onChange={handleChange}
              onBlur={handleBlur}
              value={settings.employeeIdHeader}
              error={inputError('employeeIdHeader')}
            />
          </InputWrapper> */}
          <InputWrapper>
            <TextInput
              label={t('sharing.employeeIdBody')}
              id="employeeIdBody"
              name="employeeIdBody"
              value={settings.employeeIdBody}
              onChange={handleChange}
              onBlur={handleBlur}
              error={inputError('employeeIdBody')}
              as="textarea"
              charactersLimit={maxCharacters}
              dataTip={t('sharing.employeeIdBodyDataTip')}
            />
          </InputWrapper>
        </>
      )}
    </GuestAccessBox>
  );
};

export default GuestAccessSettings;

const GuestAccessBox = styled.div`
  border-left: 4px solid #0066ff;
  background-color: #f6f8ff;
  padding: 24px;
  margin-top: 24px;
  border-radius: 4px;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const TitleText = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const Description = styled.p`
  color: #666;
  font-size: 12px;
  margin: 0 0 24px 0;
`;

const SettingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SettingEmployeeIdRow = styled(SettingRow)`
  border-left: 2px solid oklch(0.809 0.105 251.813);
`;

const SettingLabel = styled(Label)`
  display: flex;
  align-items: center;
  margin: 0;
  font-weight: 500;
`;

const EmployeeIdLabel = styled(SettingLabel)`
  padding-left: 10px;
`;

const InputWrapper = styled.div`
  margin: 0 0 30px;
  width: 100%;
  padding-left: 10px;
`;

const SeparatorLine = styled.div`
  width: 100%;
  border: 0.5px solid oklch(0.809 0.105 251.813);
  margin-top: 16px;
  margin-bottom: 11px;
`;
